import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
    makeStyles, Button,
    Dialog, DialogContent, DialogActions,
} from '@material-ui/core';
import { FetchPolicy } from 'utils/enum/Core';
import { useQuery, useMutation } from '@apollo/client';
import { Col, Row, Form } from 'react-bootstrap';
import { DOCUMENT_TYPES_AVAILABLE_MODULES, modules } from 'utils/enum/modules';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import ObstacleStyle from 'styles/modules/crm/ObstacleStyle';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import VirtualTable from 'components/widgets/VirtualTable';
import Select from 'components/widgets/Select';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DocumentTypeQuery from 'services/graphQL/query/DocumentTypeQuery';
import DocumentTypeMutation from 'services/graphQL/mutate/DocumentTypeMutation';

// Icons
import AddIcon from '@material-ui/icons/Add';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';

const useObstacleStyles = makeStyles((theme) => ObstacleStyle.content(theme));
const useStyles = makeStyles(() => ({
    tableHeight: {
        height: '70vh',
    },
    module: {
        display: 'initial !important',
    },
}));

const DocumentTypeSettings = () => {
    const classes = { ...useObstacleStyles(), ...useStyles() };
    const [state, setState] = useState({
        types: [],
        isFormOpen: false,
        isDeletePromptOpen: false,
        selectedDocumentType: null,
    });

    const {
        types,
        isFormOpen,
        isDeletePromptOpen,
        selectedDocumentType,
    } = state;

    const {
        data,
        loading,
        error,
        refetch,
    } = useQuery(DocumentTypeQuery.GET_DOCUMENT_TYPES, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        notifyOnNetworkStatusChange: true,
    });

    const toggleForm = (record) => {
        setState((prevState) => ({
            ...prevState,
            selectedDocumentType: record,
            isFormOpen: !prevState.isFormOpen,
        }));
    };

    const toggleDeletePrompt = (record) => {
        setState((prevState) => ({
            ...prevState,
            selectedDocumentType: record,
            isDeletePromptOpen: !prevState.isDeletePromptOpen,
        }));
    };

    const [createDocumentType, { loading: creatingDocumentType }] = useMutation(DocumentTypeMutation.CREATE_DOCUMENT_TYPE, {
        onCompleted: () => {
            refetch();
            ModalUtils.successMessage(null, 'Document Type saved successfully');
        },
        onError: (err) => {
            ModalUtils.errorMessage(null, err);
        },
    });

    const [updateDocumentType, { loading: updatingDocumentType }] = useMutation(DocumentTypeMutation.UPDATE_DOCUMENT_TYPE, {
        onCompleted: () => {
            refetch();
            ModalUtils.successMessage(null, 'Document Type updated successfully');
        },
        onError: (err) => {
            ModalUtils.errorMessage(null, err);
        },
    });

    const [deleteDocumentType, { loading: deletingDocumentType }] = useMutation(DocumentTypeMutation.DELETE_DOCUMENT_TYPE, {
        onCompleted: () => {
            refetch();
            ModalUtils.successMessage(null, 'Document Type deleted');
        },
        onError: (err) => {
            ModalUtils.errorMessage(null, err);
        },
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && data) {
            const { getDocumentTypes } = data;
            setState((prevState) => ({
                ...prevState,
                types: Array.isArray(getDocumentTypes) ? getDocumentTypes : [],
                selectedDocumentType: null,
                isFormOpen: false,
                isDeletePromptOpen: false,
            }));
        }
    }, [loading, error, data]);

    const onChange = (name, value) => {
        setState((prevState) => {
            const clone = { ...(prevState.selectedDocumentType ?? {}) };
            clone[name] = value;

            return {
                ...prevState,
                selectedDocumentType: clone,
            };
        });
    };

    const onSave = () => {
        if (!selectedDocumentType) return;

        const {
            documentTypeId,
            description,
            module,
        } = selectedDocumentType;

        if (documentTypeId) {
            updateDocumentType({
                variables: {
                    documentTypeId,
                    description,
                    module,
                },
            });

            return;
        }

        createDocumentType({
            variables: {
                description,
                module,
            },
        });
    };

    const isProcessing = creatingDocumentType || updatingDocumentType || deletingDocumentType;
    const columns = [
        {
            headerClassName: classes.tableHeader,
            label: '',
            dataKey: 'number',
            width: 60,
            // eslint-disable-next-line react/prop-types
            cellRenderer: ({ rowIndex }) => (
                <span>{rowIndex + 1}</span>
            ),
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Module',
            dataKey: 'module',
            width: 200,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.module}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Type',
            dataKey: 'description',
            width: 350,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.description}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Actions',
            dataKey: 'actions',
            width: 100,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;

                return (
                    <div className={classes.actionsContainer}>
                        <Button
                            disabled={isProcessing}
                            className={classes.containedInfo}
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => toggleForm(record)}
                        />
                        <Button
                            disabled={isProcessing}
                            className={classes.containedError}
                            size="small"
                            startIcon={<DeleteOutlineOutlinedIcon />}
                            onClick={() => toggleDeletePrompt(record)}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => toggleForm()}
                    className={classes.containedGreen}
                    startIcon={<AddIcon />}
                >
                    New
                </Button>
            </div>
            <div className={classes.tableGrid}>
                <div className={clsx(classes.tableContainer, classes.tableHeight, 'virtual-table-base')}>
                    <VirtualTable
                        loading={loading}
                        rowHeight={45}
                        totalRecords={types.length}
                        data={types}
                        columns={columns}
                    />
                </div>
            </div>
            {isFormOpen && (
                <Dialog
                    open
                    maxWidth="sm"
                    fullWidth
                    disableBackdropClick
                    disableEscapeKeyDown
                    scroll="paper"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <DialogAppBar
                        appBarClassName={classes.AppBar}
                        title="Document Type"
                        onClose={() => toggleForm()}
                        toolbarSize="md"
                    />
                    <DialogContent>
                        <div className={classes.content}>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.dialogLabels}>Module</Form.Label>
                                <Select
                                    nowrap
                                    size="sm"
                                    loading={false}
                                    name=""
                                    className={clsx(classes.input, classes.module, { 'invalid-field': StringUtils.isEmpty(selectedDocumentType?.module) })}
                                    onChange={(_, newValue) => onChange('module', newValue)}
                                    value={selectedDocumentType?.module ?? ''}
                                    options={Object.values(DOCUMENT_TYPES_AVAILABLE_MODULES).map((m) => {
                                        const item = [modules.CRM, modules.HR].includes(m) ? m.toUpperCase() : StringUtils.toPascalCase(m);

                                        return {
                                            label: item,
                                            value: item,
                                        };
                                    })}
                                />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.dialogLabels}>Type</Form.Label>
                                <Form.Control
                                    maxLength="50"
                                    className={clsx(classes.input, { 'invalid-field': StringUtils.isEmpty(selectedDocumentType?.description) })}
                                    type="text"
                                    name="description"
                                    value={selectedDocumentType?.description ?? ''}
                                    onChange={(e) => onChange(e.target.name, e.target.value)}
                                />
                            </Form.Group>
                        </div>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <Form.Group as={Row}>
                            <Button
                                size="small"
                                className={classes.containedSecondaryInfo}
                                disabled={
                                    isProcessing
                                    || StringUtils.isEmpty(selectedDocumentType?.module)
                                    || StringUtils.isEmpty(selectedDocumentType?.description)
                                }
                                onClick={onSave}
                            >
                                Save
                            </Button>
                        </Form.Group>
                    </DialogActions>
                </Dialog>
            )}
            {isDeletePromptOpen && (
                <ConfirmDialog
                    open
                    title="Attention"
                    description={`Do you want to remove this document type: ${selectedDocumentType.description}?`}
                    variant="outlined"
                    titlePrimary="Remove"
                    titleSecondary="Cancel"
                    onClose={() => toggleDeletePrompt()}
                    onClickSecondary={() => toggleDeletePrompt()}
                    onClickPrimary={() => deleteDocumentType({ variables: { documentTypeId: selectedDocumentType.documentTypeId } })}
                />
            )}
        </div>
    );
};

export default DocumentTypeSettings;
